// src/components/BacklogTaskItem.js

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Square, CheckSquare, Calendar, Trash2 } from 'lucide-react';
import { DEFAULT_PROJECT_PRIORITY_VALUE, getProjectPriorityInfo } from '../utils/projectPriority';

const priorityMap = {
    urgent: { label: 'Urgente', color: 'bg-red-500' },
    high: { label: 'Haute', color: 'bg-orange-400' },
    medium: { label: 'Moyenne', color: 'bg-yellow-400' },
    low: { label: 'Basse', color: 'bg-blue-400' },
};

const BacklogTaskItem = ({ task, onUpdate, onDelete, objectives, employees, projects }) => {
    const [editingField, setEditingField] = useState(null);
    const [editingValue, setEditingValue] = useState('');

    const handleEditField = (e, field, value) => {
        e.stopPropagation();
        setEditingField(field);
        let initialValue = value || '';
        if (field === 'due_date' && initialValue) {
            initialValue = new Date(initialValue).toISOString().split('T')[0];
        }
        setEditingValue(initialValue);
    };

    const handleFieldChange = (e) => setEditingValue(e.target.value);

    // Sauvegarde pour les champs texte (déclenchée par onBlur)
    const handleSaveField = () => {
        if (editingField) {
            const originalValue = task[editingField] || '';
            const newValue = editingValue === '' ? null : editingValue;
            if (String(originalValue) !== String(newValue)) {
                onUpdate(task.id, { [editingField]: newValue });
            }
        }
        setEditingField(null);
    };

    const handleSelectSave = (e) => {
        const value = e.target.value;
        const field = editingField;

        if (field) {
            const newValue = value === '' ? null : value;
            onUpdate(task.id, { [field]: newValue });
        }
        setEditingField(null);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') e.target.blur();
        if (e.key === 'Escape') setEditingField(null);
    };

    const priorityInfo = task.priority ? priorityMap[task.priority] : null;
    const isDueDatePast = task.due_date && new Date(task.due_date) < new Date() && !task.is_completed;
    const objectiveForTask = objectives.find(o => String(o.id) === String(task.objective_id));
    const projectForObjective = objectiveForTask && projects ? projects.find(p => String(p.id) === String(objectiveForTask.project_id)) : null;
    const projectForTask = projectForObjective || (task.project_id && projects ? projects.find(p => String(p.id) === String(task.project_id)) : null);
    const projectPriorityInfo = projectForTask
        ? getProjectPriorityInfo(projectForTask.pilotage_priority, DEFAULT_PROJECT_PRIORITY_VALUE)
        : null;
    const owner = task.owner_id ? employees.find(e => e.id === task.owner_id) : null;

    return (
        <div 
            draggable 
            onDragStart={(e) => e.dataTransfer.setData('application/json', JSON.stringify(task))}
            className="group bg-white p-2 rounded shadow-sm border border-gray-200 cursor-grab active:cursor-grabbing"
            style={{ touchAction: 'pan-y' }}
        >
            <div className="flex flex-col 2xl:flex-row 2xl:items-start 2xl:justify-between">
                <div className="flex items-start flex-grow min-w-0 mb-2 2xl:mb-0">
                    <button onClick={() => onUpdate(task.id, { is_completed: !task.is_completed })} className="mr-2 mt-0.5 flex-shrink-0">
                        {task.is_completed ? <CheckSquare className="text-green-500"/> : <Square className="text-gray-400"/>}
                    </button>
                    
                    <div className="flex-grow min-w-0">
                        {editingField === 'name' ? (
                            <input type="text" value={editingValue} onChange={handleFieldChange} onBlur={handleSaveField} onKeyDown={handleKeyDown} className="w-full border-b text-sm" autoFocus/>
                        ) : (
                            <p onClick={(e) => handleEditField(e, 'name', task.name)} className={`text-sm text-gray-800 cursor-pointer ${task.is_completed ? 'line-through text-gray-500' : ''}`}>{task.name}</p>
                        )}
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                        {task.objective_id && 
                            <Link to={`/objectives?project=${objectiveForTask?.project_id}&objective=${task.objective_id}`} className="text-xs text-blue-600 hover:underline truncate inline-flex items-center" title={`Objectif : ${task.objective_name}`}>
                                <span className="truncate">{task.objective_name || `#${task.objective_id}`}</span>
                                {projectForObjective && <span className="ml-2 text-gray-400 font-light truncate">#{projectForObjective.name}</span>}
                            </Link>
                        }
                        {projectPriorityInfo && (
                            <span
                                title={`Priorite projet : P${projectPriorityInfo.value} - ${projectPriorityInfo.label}`}
                                className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${projectPriorityInfo.className}`}
                            >
                                P{projectPriorityInfo.value}
                            </span>
                        )}
                        </div>
                    </div>
                </div>

                <div className="flex items-center space-x-2 flex-shrink-0 2xl:ml-4">
                    <button onClick={() => onDelete(task.id)} className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Supprimer la tâche">
                        <Trash2 size={14} />
                    </button>
                    {/* Priorité */}
                    <div onClick={(e) => handleEditField(e, 'priority', task.priority)}>
                        {editingField === 'priority' ? (
                            <select value={editingValue} onChange={handleSelectSave} onBlur={() => setEditingField(null)} onKeyDown={handleKeyDown} className="text-xs border rounded h-5" autoFocus>
                                <option value="">--</option>
                                <option value="low">Basse</option>
                                <option value="medium">Moyenne</option>
                                <option value="high">Haute</option>
                                <option value="urgent">Urgente</option>
                            </select>
                        ) : (
                            priorityInfo ? <div title={`Priorité: ${priorityInfo.label}`} className={`h-2 w-4 rounded-full cursor-pointer ${priorityInfo.color}`}></div> : <div title="Définir la priorité" className="h-2 w-4 rounded-full cursor-pointer border-dashed border border-gray-400"></div>
                        )}
                    </div>
                    {/* Date Limite */}
                    <div onClick={(e) => handleEditField(e, 'due_date', task.due_date)}>
                         {editingField === 'due_date' ? (
                            <input type="date" value={editingValue} onChange={handleFieldChange} onBlur={handleSaveField} onKeyDown={handleKeyDown} className="text-xs border rounded h-5 w-28" autoFocus/>
                         ) : (
                            <div className={`flex items-center text-xs cursor-pointer ${isDueDatePast ? 'text-red-500 font-semibold' : 'text-gray-500'}`} title="Date limite">
                                <Calendar size={12} className="mr-1"/>
                                {task.due_date ? new Date(task.due_date).toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit'}) : <span className="text-gray-300">Date</span>}
                            </div>
                         )}
                    </div>
                    {/* Owner */}
                    <div onClick={(e) => handleEditField(e, 'owner_id', task.owner_id)}>
                        {editingField === 'owner_id' ? (
                            <select value={editingValue} onChange={handleSelectSave} onBlur={() => setEditingField(null)} onKeyDown={handleKeyDown} className="text-xs border rounded h-5" autoFocus>
                                <option value="">--</option>
                                {employees.map(emp => (<option key={emp.id} value={emp.id}>{emp.name}</option>))}
                            </select>
                        ) : (
                            <div className="h-5 w-5 rounded-full bg-gray-200 text-gray-700 text-[9px] cursor-pointer flex items-center justify-center font-bold" title={owner ? owner.name : "Assigner"}>
                                {owner ? owner.name.split(' ').map(n=>n[0]).join('') : '?'}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BacklogTaskItem;
